import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Store } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { AddToProductComponent } from 'src/app/barcode/add-to-product/add-to-product.component';
import { ComputerService } from 'src/app/computer/computer.service';
import { LabelPrintComponent } from 'src/app/computer/label-print/label-print.component';
import { addToCart, removeFromCart, updateProductInCart } from '../../redux/actions/cart.actions';
import {
  NewCatalogWarehouseLegend,
  Product,
  ProductAnalogueSupplierSource,
  ProductAnalogueSupplierGroupSelectionRequiredError,
  ProductAnalogueSyncStatus,
  ProductAnalogueV2Item,
  ProductAnaloguesV2Response,
  ProductImportFromExistCandidate,
  ProductImportFromExistImportedResult,
  ProductInCart,
  ProductInProviderWarehouse,
  ProductInWarehouse,
  User
} from 'src/typings';
import { environment } from 'src/environments/environment';
import { AppState } from '../../redux/app.state';
import { NotificationService } from '../../services/notification.service';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { AddImageComponent } from '../add-image/add-image.component';
import { AnaloguePanelV2DialogComponent } from './analogue-panel-v2-dialog.component';
import { CatalogStockDialogComponent } from '../catalog-stock-dialog/catalog-stock-dialog.component';
import { ExistImportSelectionDialogComponent } from '../exist-import-selection-dialog/exist-import-selection-dialog.component';
import { FoldersComponent } from '../folders/folders.component';
import { PropertiesComponent } from '../info/properties/properties.component';
import { MoveComponent } from '../move/move.component';
import { NameUpdateComponent } from '../name-update/name-update.component';
import { RemoveDoubleComponent } from '../remove-double/remove-double.component';
import { ProductService } from '../product.service';
import { SetNewPriceComponent } from '../set-new-price/set-new-price.component';
import { AnalogueManualPickerDialogComponent } from './analogue-manual-picker-dialog.component';
import { AnalogueSupplierGroupSelectDialogComponent } from './analogue-supplier-group-select-dialog.component';

type AnalogueTabKey = 'all' | 'manual' | 'supplier' | 'oe';

interface AnalogueProviderTile {
  key: 'in_city' | 'out_of_city';
  label: string;
  quantity: number;
  quantityLabel: string;
  tooltip: string;
}

interface AnalogueProviderTooltipDetail {
  providerName: string;
  quantity: number;
  price: number | null;
}

interface AnalogueStockTile {
  warehouseId: number;
  warehouseName: string;
  quantity: number;
  isPrimary: boolean;
  quantityLabel: string;
  tooltip: string;
}

type AnalogueAvailabilityBucket =
  | 'MANAGER_STOCK'
  | 'OTHER_STOCK'
  | 'PROVIDER_IN_CITY'
  | 'PROVIDER_OUT_OF_CITY'
  | 'NO_STOCK';

interface AnalogueSortMetrics {
  bucket: AnalogueAvailabilityBucket;
  managerStockTotal: number;
  otherStockTotal: number;
  providerInCityTotal: number;
  providerOutOfCityTotal: number;
  stockTotal: number;
  displayPrice: number;
}

@Component({
  selector: 'app-analogue-panel-v2',
  templateUrl: './analogue-panel-v2.component.html',
  styleUrls: ['./analogue-panel-v2.component.scss']
})
export class AnaloguePanelV2Component implements OnChanges, OnDestroy {
  @Input() productId?: number | null;

  @Input() productTitle = '';

  @Input() embedded = false;

  @Input() hideHeader = false;

  @Input() dialogMode = false;

  @Input() cardMode = false;

  readonly tabs: Array<{ key: AnalogueTabKey; label: string }> = [
    { key: 'all', label: 'Все' },
    { key: 'manual', label: 'Ручные' },
    { key: 'supplier', label: 'Поставщики' },
    { key: 'oe', label: 'OE' }
  ];

  activeTab: AnalogueTabKey = 'all';

  loading = false;

  errorMessage = '';

  response?: ProductAnaloguesV2Response;

  readonly imagePreviewSizePx = 260;

  readonly imagePreviewOffsetPx = 20;

  selectedContractIds: number[] = [];

  user?: User;

  hoveredImageUrl: string | null = null;

  hoveredImageTitle = '';

  hoveredImagePreviewLeft = 0;

  hoveredImagePreviewTop = 0;

  private readonly syncingContractIds = new Set<number>();

  private readonly loadingSupplierContractIds = new Set<number>();

  private readonly loadingManualIds = new Set<number>();

  private readonly excludingSupplierIds = new Set<number>();

  private readonly cartLoadingIds = new Set<number>();

  private readonly importingFromExistIds = new Set<number>();

  private readonly togglingPublicIds = new Set<number>();

  private readonly togglingUpdateRetailIds = new Set<number>();

  private cartProducts: ProductInCart[] = [];

  private analogueSubscription?: Subscription;

  private readonly storeSubscription: Subscription;

  private readonly productDetailsCache = new Map<number, Product>();

  private readonly stockTilesCache = new Map<number, AnalogueStockTile[]>();

  private readonly providerTilesCache = new Map<number, AnalogueProviderTile[]>();

  @ViewChild(MatMenuTrigger, { static: true })
  contextMenuTrigger?: MatMenuTrigger;

  contextMenuPosition = {
    x: '0px',
    y: '0px'
  };

  constructor(
    private readonly productService: ProductService,
    private readonly dialog: MatDialog,
    private readonly computerService: ComputerService,
    private readonly notificationService: NotificationService,
    private readonly store: Store<AppState>
  ) {
    this.storeSubscription = this.store.subscribe((state) => {
      this.user = state.user.info;
      this.cartProducts = state.cart.map((prod: any) => ({
        ...prod,
        quan: Number(prod.quan)
      }));
      this.clearTileCaches();
    });

    this.analogueSubscription = this.productService.analgogueV2Gw.subscribe((payload) => {
      if (!this.productId) {
        return;
      }
      if (payload.productIds?.includes(this.productId)) {
        void this.reload();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.productId && this.productId) {
      void this.reload(true);
    }
  }

  ngOnDestroy(): void {
    this.analogueSubscription?.unsubscribe();
    this.storeSubscription.unsubscribe();
  }

  get visibleItems(): ProductAnalogueV2Item[] {
    if (!this.response) {
      return [];
    }

    if (this.activeTab === 'manual') {
      return this.response.manual;
    }

    if (this.activeTab === 'supplier') {
      return this.response.supplier;
    }

    if (this.activeTab === 'oe') {
      return this.response.oe;
    }

    const merged = new Map<number, ProductAnalogueV2Item>();
    [...this.response.manual, ...this.response.supplier, ...this.response.oe].forEach((item) => {
      const existing = merged.get(item.product.id);
      if (!existing) {
        merged.set(item.product.id, {
          ...item,
          supplierSources: [...(item.supplierSources || [])]
        });
        return;
      }

      const sourceMap = new Map(
        [...existing.supplierSources, ...(item.supplierSources || [])].map((source) => [
          `${source.contractId}:${source.providerCode}`,
          source
        ])
      );

      merged.set(item.product.id, {
        ...existing,
        relationType:
          existing.relationType === 'ANALOG' ? existing.relationType : item.relationType,
        sourceKind: existing.sourceKind === 'MANUAL' ? existing.sourceKind : item.sourceKind,
        supplierSources: [...sourceMap.values()]
      });
    });

    return [...merged.values()].sort((left, right) => this.sortItems(left, right));
  }

  getTabCount(key: AnalogueTabKey) {
    if (!this.response) {
      return 0;
    }

    switch (key) {
      case 'manual':
        return this.response.manual.length;
      case 'supplier':
        return this.response.supplier.length;
      case 'oe':
        return this.response.oe.length;
      case 'all':
      default:
        return new Set(
          [...this.response.manual, ...this.response.supplier, ...this.response.oe].map(
            (item) => item.product.id
          )
        ).size;
    }
  }

  getProductImageUrl(item: ProductAnalogueV2Item): string | null {
    const image = item.product.images?.[0];
    const imageName = String(image?.name || '').trim();

    if (/^(https?:|data:image\/)/i.test(imageName)) {
      return imageName;
    }

    if (imageName && !this.isRemoteImageReferenceName(imageName)) {
      return `${environment.BACKEND}/image/${imageName}`;
    }

    const supplierImageUrl = this.getPublicSupplierImageUrl(image?.url);
    if (supplierImageUrl) {
      return supplierImageUrl;
    }

    const directUrl = String(image?.url || '').trim();
    if (/^(https?:|data:image\/)/i.test(directUrl)) {
      return directUrl;
    }

    return imageName ? `${environment.BACKEND}/image/${imageName}` : null;
  }

  showImagePreview(event: MouseEvent, imageUrl: string | null, title?: string | null): void {
    if (!imageUrl) return;

    this.hoveredImageUrl = imageUrl;
    this.hoveredImageTitle = title || '';
    this.updateImagePreviewPosition(event);
  }

  moveImagePreview(event: MouseEvent): void {
    if (!this.hoveredImageUrl) return;
    this.updateImagePreviewPosition(event);
  }

  hideImagePreview(): void {
    this.hoveredImageUrl = null;
    this.hoveredImageTitle = '';
  }

  private getPublicSupplierImageUrl(imageUrl?: string | null): string | null {
    const normalizedUrl = String(imageUrl || '').trim();
    const match = normalizedUrl.match(/^https?:\/\/ecom\.ad\.ua\/tcd\/(.+)$/i);
    if (!match?.[1]) {
      return null;
    }

    return `https://img2.ad.ua/imgs/tcd-pic/${match[1]}`;
  }

  private isRemoteImageReferenceName(imageName?: string | null): boolean {
    return String(imageName || '').startsWith('supplier-remote-');
  }

  getBrandLogoUrl(item: ProductAnalogueV2Item): string | null {
    const logo = item.product.brand?.logo;
    return logo ? `${environment.BACKEND}/image/${logo}` : null;
  }

  getDisplayPrice(product: Product): number {
    return Number(product.retail ?? product.price ?? 0);
  }

  formatPriceHryvnia(value: number | null | undefined): string {
    const normalizedValue = Number(value ?? 0);
    return `${normalizedValue.toLocaleString('uk-UA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} грн.`;
  }

  getDisplayPriceTooltip(product: Product): string {
    if (product.retail === null || product.retail === undefined) {
      return '';
    }

    if (product.price === null || product.price === undefined) {
      return '';
    }

    return `${this.formatPriceHryvnia(product.price)}`;
  }

  getStockTiles(item: ProductAnalogueV2Item): AnalogueStockTile[] {
    const cached = this.stockTilesCache.get(item.product.id);
    if (cached) {
      return cached;
    }

    const warehouseLegend = this.getWarehouseLegend();
    if (!warehouseLegend.length) {
      const tiles = (item.product.stock || []).map((stock) => ({
        warehouseId: Number(stock.warehouse?.id || 0),
        warehouseName: stock.warehouse?.description || 'Склад',
        quantity: Number(stock.quan || 0),
        isPrimary:
          Number(stock.warehouse?.id || 0) === Number(this.user?.defaultWarehouse?.id || 0),
        quantityLabel: this.formatStockQuantity(stock.quan),
        tooltip: stock.warehouse?.description || 'Склад'
      }));
      this.stockTilesCache.set(item.product.id, tiles);
      return tiles;
    }

    const stockMap = new Map<number, ProductInWarehouse>(
      (item.product.stock || []).map((stock) => [Number(stock.warehouse?.id), stock])
    );
    const primaryWarehouseId = Number(this.user?.defaultWarehouse?.id || 0) || null;

    const tiles = warehouseLegend.map((warehouse) => {
      const stock = stockMap.get(warehouse.warehouseId);
      const quantity = Number(stock?.quan || 0);
      const isPrimary =
        (primaryWarehouseId !== null && warehouse.warehouseId === primaryWarehouseId) ||
        warehouse.isPrimaryForManager;
      const citySuffix = warehouse.cityName ? ` • ${warehouse.cityName}` : '';

      return {
        warehouseId: warehouse.warehouseId,
        warehouseName: warehouse.warehouseName,
        quantity,
        isPrimary,
        quantityLabel: this.formatStockQuantity(quantity),
        tooltip: `${warehouse.warehouseName}${citySuffix}${isPrimary ? ' • основной склад' : ''}`
      };
    });
    this.stockTilesCache.set(item.product.id, tiles);
    return tiles;
  }

  getPrimaryStock(item: ProductAnalogueV2Item): ProductInWarehouse | null {
    const stocks = item.product.stock || [];
    if (!stocks.length) {
      return null;
    }

    const defaultWarehouseId = Number(this.user?.defaultWarehouse?.id || 0);
    const primary = stocks.find((row) => Number(row.warehouse?.id) === defaultWarehouseId);
    return primary || stocks.find((row) => !!row.cell) || stocks[0] || null;
  }

  getProviderTiles(item: ProductAnalogueV2Item): AnalogueProviderTile[] {
    const cached = this.providerTilesCache.get(item.product.id);
    if (cached) {
      return cached;
    }

    const providerStock = item.product.providerStock || [];
    const cityId = this.getManagerCityId();

    const inCityRows = providerStock.filter(
      (row) => cityId !== null && Number(row.providerWarehouse?.city?.id || 0) === cityId
    );
    const outOfCityRows =
      cityId !== null
        ? providerStock.filter((row) => Number(row.providerWarehouse?.city?.id || 0) !== cityId)
        : providerStock;

    const tiles = [
      this.buildProviderTile('in_city', 'В городе', inCityRows),
      this.buildProviderTile('out_of_city', 'За городом', outOfCityRows)
    ];
    this.providerTilesCache.set(item.product.id, tiles);
    return tiles;
  }

  getBrandRatingRange(item: ProductAnalogueV2Item) {
    return Array.from({ length: Number(item.product.brand?.rating || 0) });
  }

  getBrandRatingValue(item: ProductAnalogueV2Item) {
    return Number(item.product.brand?.rating || 0);
  }

  isSyncing(contractId: number) {
    return this.syncingContractIds.has(contractId);
  }

  isSupplierLoading(contractId: number) {
    return this.loadingSupplierContractIds.has(contractId);
  }

  isManualLoading(productId: number) {
    return this.loadingManualIds.has(productId);
  }

  isExcludingSupplier(productId: number) {
    return this.excludingSupplierIds.has(productId);
  }

  getSupplierStatus(contractId: number): ProductAnalogueSyncStatus | undefined {
    return this.response?.syncStatus?.find(
      (item) => Number(item.contractId) === Number(contractId)
    );
  }

  get loadableSupplierSources(): ProductAnalogueSupplierSource[] {
    return (this.response?.availableSuppliers || []).filter(
      (item) => item.isEnabled && item.supportsLoadByArticle
    );
  }

  get supplierFilterSources(): ProductAnalogueSupplierSource[] {
    return (this.response?.availableSuppliers || []).filter((item) => item.isEnabled);
  }

  get primaryLoadableSupplierSource(): ProductAnalogueSupplierSource | null {
    return this.loadableSupplierSources[0] || null;
  }

  hasLoadableSupplierSources(): boolean {
    return this.loadableSupplierSources.length > 0;
  }

  hasMultipleLoadableSupplierSources(): boolean {
    return this.loadableSupplierSources.length > 1;
  }

  isAnySupplierLoading(): boolean {
    return this.loadingSupplierContractIds.size > 0;
  }

  isPrimarySupplierLoading(): boolean {
    const source = this.primaryLoadableSupplierSource;
    return source ? this.isSupplierLoading(source.contractId) : false;
  }

  isSupplierFilterSelected(contractId: number): boolean {
    return this.selectedContractIds.includes(contractId);
  }

  getSupplierFilterLabel(): string {
    const sources = this.supplierFilterSources;
    if (!sources.length) {
      return 'Интеграции';
    }

    if (!this.selectedContractIds.length || this.selectedContractIds.length === sources.length) {
      return `Интеграции: все ${sources.length}`;
    }

    if (this.selectedContractIds.length === 1) {
      const selectedSource = sources.find(
        (item) => item.contractId === this.selectedContractIds[0]
      );
      return selectedSource?.providerName || 'Интеграции: 1';
    }

    return `Интеграции: ${this.selectedContractIds.length} из ${sources.length}`;
  }

  async toggleSupplierFilter(contractId: number): Promise<void> {
    if (this.isSupplierFilterSelected(contractId)) {
      if (this.selectedContractIds.length <= 1) {
        this.notificationService.warning('Нужно оставить хотя бы одну интеграцию для показа.');
        return;
      }
      this.selectedContractIds = this.selectedContractIds.filter((id) => id !== contractId);
    } else {
      this.selectedContractIds = [...this.selectedContractIds, contractId];
    }

    await this.onSupplierSelectionChange();
  }

  async loadAllSupplierAnalogues(): Promise<void> {
    if (!this.productId) {
      return;
    }

    const sources = this.loadableSupplierSources;
    if (!sources.length) {
      this.notificationService.warning(
        'Нет доступной интеграции поставщика для загрузки аналогов.'
      );
      return;
    }

    sources.forEach((source) => this.loadingSupplierContractIds.add(source.contractId));
    try {
      const response = await firstValueFrom(
        this.productService.loadSupplierAnaloguesV2({
          source_product_id: this.productId
        })
      );

      this.applyLoadedSupplierResponse(response);
    } catch (error) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось загрузить аналоги поставщиков.')
      );
    } finally {
      sources.forEach((source) => this.loadingSupplierContractIds.delete(source.contractId));
    }
  }

  async loadPrimarySupplierAnalogues(): Promise<void> {
    const source = this.primaryLoadableSupplierSource;
    if (!source) {
      this.notificationService.warning(
        'Нет доступной интеграции поставщика для загрузки аналогов.'
      );
      return;
    }

    await this.loadSupplierAnalogues(source);
  }

  isAddingToCart(productId: number): boolean {
    return this.cartLoadingIds.has(productId);
  }

  isImportingFromExist(productId: number): boolean {
    return this.importingFromExistIds.has(productId);
  }

  isTogglingPublic(productId: number): boolean {
    return this.togglingPublicIds.has(productId);
  }

  isTogglingUpdateRetail(productId: number): boolean {
    return this.togglingUpdateRetailIds.has(productId);
  }

  get isAdmin(): boolean {
    return this.user?.roles?.some((role) => role.description === 'admin') === true;
  }

  onRightClick(event: MouseEvent, item: ProductAnalogueV2Item): void {
    if (!this.contextMenuTrigger) {
      return;
    }

    event.preventDefault();
    this.contextMenuPosition.x = `${event.clientX}px`;
    this.contextMenuPosition.y = `${event.clientY}px`;
    this.contextMenuTrigger.menuData = { item };
    this.contextMenuTrigger.openMenu();
  }

  async reload(forceResetSelection = false) {
    if (!this.productId) {
      this.response = undefined;
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.clearTileCaches();
    try {
      const response = await firstValueFrom(
        this.productService.getAnaloguesV2(
          this.productId,
          forceResetSelection ? [] : this.selectedContractIds
        )
      );

      this.response = response;
      const nextAvailableIds = response.availableSuppliers
        .filter((item) => item.isEnabled)
        .map((item) => item.contractId);
      if (
        forceResetSelection ||
        !this.selectedContractIds.length ||
        this.selectedContractIds.some((id) => !nextAvailableIds.includes(id))
      ) {
        this.selectedContractIds = [...nextAvailableIds];
        if (!forceResetSelection && nextAvailableIds.length) {
          this.response = await firstValueFrom(
            this.productService.getAnaloguesV2(this.productId, this.selectedContractIds)
          );
        }
      }
    } catch (error) {
      this.errorMessage = extractApiErrorMessage(error, 'Не удалось загрузить аналоги.');
    } finally {
      this.loading = false;
    }
  }

  async onSupplierSelectionChange() {
    await this.reload();
  }

  async openAddManualDialog() {
    if (!this.productId) {
      return;
    }

    const excludeProductIds = [
      this.productId,
      ...(this.response?.manual || []).map((item) => item.product.id),
      ...(this.response?.supplier || []).map((item) => item.product.id),
      ...(this.response?.oe || []).map((item) => item.product.id)
    ];

    const dialogRef = this.dialog.open(AnalogueManualPickerDialogComponent, {
      width: 'min(1100px, calc(100vw - 48px))',
      maxWidth: '95vw',
      data: {
        sourceProductId: this.productId,
        excludeProductIds
      }
    });

    const changed = await firstValueFrom(dialogRef.afterClosed());
    if (changed) {
      await this.reload();
    }
  }

  async removeManual(item: ProductAnalogueV2Item) {
    if (!this.productId) {
      return;
    }

    this.loadingManualIds.add(item.product.id);
    try {
      await firstValueFrom(
        this.productService.removeManualAnalogueV2({
          source_product_id: this.productId,
          target_product_id: item.product.id
        })
      );
      this.notificationService.notice('Ручной аналог удалён.');
      await this.reload();
    } catch (error) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось удалить аналог.')
      );
    } finally {
      this.loadingManualIds.delete(item.product.id);
    }
  }

  async promoteToManual(item: ProductAnalogueV2Item) {
    if (!this.productId) {
      return;
    }

    this.loadingManualIds.add(item.product.id);
    try {
      await firstValueFrom(
        this.productService.addManualAnalogueV2({
          source_product_id: this.productId,
          target_product_id: item.product.id
        })
      );
      this.notificationService.notice('Связь добавлена в ручные аналоги.');
      this.activeTab = 'manual';
      await this.reload();
    } catch (error) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось добавить ручной аналог.')
      );
    } finally {
      this.loadingManualIds.delete(item.product.id);
    }
  }

  async excludeSupplierAnalogue(item: ProductAnalogueV2Item) {
    if (!this.productId) {
      return;
    }

    const sources = item.supplierSources || [];
    if (!sources.length) {
      this.notificationService.warning('Не найден источник поставщика для этой связи.');
      return;
    }

    this.excludingSupplierIds.add(item.product.id);
    try {
      let latestResponse: ProductAnaloguesV2Response | null = null;
      for (const source of sources) {
        latestResponse = await firstValueFrom(
          this.productService.excludeSupplierAnalogueV2({
            source_product_id: this.productId,
            target_product_id: item.product.id,
            contract_id: source.contractId,
            provider_code: source.providerCode,
            relation_type: item.relationType
          })
        );
      }

      if (latestResponse) {
        this.response = latestResponse;
      }
      this.clearTileCaches();
      this.notificationService.notice('Аналог поставщика исключён из загрузки.');
      await this.reload();
    } catch (error) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось исключить аналог поставщика.')
      );
    } finally {
      this.excludingSupplierIds.delete(item.product.id);
    }
  }

  async runSupplierSync(contractId: number) {
    this.syncingContractIds.add(contractId);
    try {
      await firstValueFrom(this.productService.runSupplierAnalogueSync(contractId));
      this.notificationService.notice('Синхронизация аналогов завершена.');
      await this.reload();
    } catch (error) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось синхронизировать аналоги поставщика.')
      );
    } finally {
      this.syncingContractIds.delete(contractId);
    }
  }

  async loadSupplierAnalogues(source: ProductAnalogueSupplierSource) {
    if (!this.productId) {
      return;
    }

    this.loadingSupplierContractIds.add(source.contractId);
    try {
      let selectedSubGroupCode: string | undefined;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await firstValueFrom(
            this.productService.loadSupplierAnaloguesV2({
              source_product_id: this.productId,
              contract_id: source.contractId,
              ...(selectedSubGroupCode
                ? {
                    selected_sub_group_code: selectedSubGroupCode
                  }
                : {})
            })
          );

          this.applyLoadedSupplierResponse(response);
          return;
        } catch (error) {
          const selectionRequest = this.extractSupplierGroupSelectionRequest(error, source);
          if (attempt === 0 && selectionRequest) {
            const pickedSubGroupCode = await this.pickSupplierSubGroupCode(
              source,
              selectionRequest
            );
            if (!pickedSubGroupCode) {
              return;
            }

            selectedSubGroupCode = pickedSubGroupCode;
            continue;
          }

          this.notificationService.showError(
            extractApiErrorMessage(error, 'Не удалось загрузить аналоги поставщика.')
          );
          return;
        }
      }
    } finally {
      this.loadingSupplierContractIds.delete(source.contractId);
    }
  }

  private applyLoadedSupplierResponse(response: ProductAnaloguesV2Response) {
    this.response = response;
    this.selectedContractIds = (response.availableSuppliers || [])
      .filter((item) => item.isEnabled)
      .map((item) => item.contractId);
    this.clearTileCaches();

    this.activeTab = 'all';

    if (response.supplier.length || response.oe.length) {
      this.notificationService.notice(
        `Загружено: аналоги ${response.supplier.length}, OE ${response.oe.length}.`
      );
      return;
    }

    this.notificationService.warning(
      'Поставщик ответил, но локальных совпадений по аналогам не найдено.'
    );
  }

  private extractSupplierGroupSelectionRequest(
    error: unknown,
    source: ProductAnalogueSupplierSource
  ): ProductAnalogueSupplierGroupSelectionRequiredError | null {
    const payload = (error as any)?.error;
    if (!payload || payload.code !== 'SUPPLIER_GROUP_SELECTION_REQUIRED') {
      return null;
    }

    const availableSubGroupCodes = Array.isArray(payload.availableSubGroupCodes)
      ? payload.availableSubGroupCodes
          .map((value: unknown) => String(value || '').trim())
          .filter((value: string) => Boolean(value))
      : [];
    if (!availableSubGroupCodes.length) {
      return null;
    }

    return {
      code: 'SUPPLIER_GROUP_SELECTION_REQUIRED',
      message: String(payload.message || '').trim() || 'Выберите группу аналогов поставщика.',
      providerCode: String(payload.providerCode || source.providerCode),
      contractId: Number(payload.contractId || source.contractId),
      lookupArticle: String(payload.lookupArticle || '').trim(),
      availableSubGroupCodes: Array.from(new Set(availableSubGroupCodes))
    };
  }

  private async pickSupplierSubGroupCode(
    source: ProductAnalogueSupplierSource,
    payload: ProductAnalogueSupplierGroupSelectionRequiredError
  ) {
    const dialogRef = this.dialog.open(AnalogueSupplierGroupSelectDialogComponent, {
      width: 'min(560px, calc(100vw - 32px))',
      maxWidth: '95vw',
      data: {
        providerName: source.providerName,
        contractNumber: source.contractNumber || null,
        productTitle: this.productTitle,
        lookupArticle: payload.lookupArticle,
        availableSubGroupCodes: payload.availableSubGroupCodes
      }
    });

    const selectedSubGroupCode = await firstValueFrom(dialogRef.afterClosed());
    return typeof selectedSubGroupCode === 'string' && selectedSubGroupCode.trim()
      ? selectedSubGroupCode.trim()
      : null;
  }

  async copyArticle(item: ProductAnalogueV2Item): Promise<void> {
    const article = String(item.product.article || '').trim();
    if (!article) {
      this.notificationService.warning('Артикул пустой');
      return;
    }

    const copied = await this.writeToClipboard(article);
    if (copied) {
      this.notificationService.notice(`Артикул ${article} скопирован`);
      return;
    }

    this.notificationService.warning('Не удалось скопировать артикул');
  }

  async copyProductName(item: ProductAnalogueV2Item): Promise<void> {
    const name = String(item.product.description || '').trim();
    if (!name) {
      this.notificationService.warning('Наименование пустое');
      return;
    }

    const copied = await this.writeToClipboard(name);
    if (copied) {
      this.notificationService.notice('Наименование скопировано');
      return;
    }

    this.notificationService.warning('Не удалось скопировать наименование');
  }

  async copyBrandName(item: ProductAnalogueV2Item): Promise<void> {
    const brandName = String(item.product.brand?.description || '').trim();
    if (!brandName) {
      this.notificationService.warning('Производитель пустой');
      return;
    }

    const copied = await this.writeToClipboard(brandName);
    if (copied) {
      this.notificationService.notice('Производитель скопирован');
      return;
    }

    this.notificationService.warning('Не удалось скопировать производителя');
  }

  async copyProductSummary(item: ProductAnalogueV2Item): Promise<void> {
    const summary = this.buildProductSummaryClipboardText(item);
    if (!summary) {
      this.notificationService.warning('Данные товара пустые');
      return;
    }

    const copied = await this.writeToClipboard(summary);
    if (copied) {
      this.notificationService.notice('Данные товара скопированы');
      return;
    }

    this.notificationService.warning('Не удалось скопировать данные товара');
  }

  openAnalogies(item: ProductAnalogueV2Item): void {
    this.dialog.open(AnaloguePanelV2DialogComponent, {
      width: 'min(1660px, calc(100vw - 24px))',
      maxWidth: '99vw',
      maxHeight: 'calc(100vh - 24px)',
      data: {
        productId: item.product.id,
        productTitle: item.product.description
      }
    });
  }

  openContextAnalogies(item: ProductAnalogueV2Item): void {
    this.openAnalogies(item);
  }

  openContextMove(item: ProductAnalogueV2Item): void {
    this.openMove(item);
  }

  openContextSetNewPrice(item: ProductAnalogueV2Item): void {
    void this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(SetNewPriceComponent);
        dialogRef.componentInstance.product = product;
        const subs = dialogRef.componentInstance.saveEvent.subscribe((value: boolean) => {
          if (value) {
            dialogRef.close();
            void this.reload();
          }
          subs.unsubscribe();
        });
      },
      'Не удалось открыть изменение цены'
    );
  }

  openContextUpdateName(item: ProductAnalogueV2Item): void {
    void this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(NameUpdateComponent);
        dialogRef.componentInstance.product = product;
        dialogRef.componentInstance.doneEvent.subscribe((value: boolean) => {
          if (value) {
            dialogRef.close();
            void this.reload();
          }
        });
      },
      'Не удалось открыть изменение наименования'
    );
  }

  openContextAddImage(item: ProductAnalogueV2Item): void {
    void this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(AddImageComponent);
        dialogRef.componentInstance.product = product;
        dialogRef.componentInstance.doneEvent.subscribe((value: boolean) => {
          if (value) {
            dialogRef.close();
            void this.reload();
          }
        });
      },
      'Не удалось открыть добавление изображения'
    );
  }

  openContextRemoveDouble(item: ProductAnalogueV2Item): void {
    void this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(RemoveDoubleComponent);
        dialogRef.componentInstance.product = product;
        dialogRef.componentInstance.doneEvent.subscribe((value: boolean) => {
          if (value) {
            dialogRef.close();
            void this.reload();
          }
        });
      },
      'Не удалось открыть слияние карточек'
    );
  }

  openContextAddBarcode(item: ProductAnalogueV2Item): void {
    void this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(AddToProductComponent);
        dialogRef.componentInstance.product = product;
        dialogRef.componentInstance.doneEvent.subscribe(() => {
          dialogRef.close();
          this.productDetailsCache.delete(item.product.id);
          void this.reload();
        });
      },
      'Не удалось открыть добавление штрих-кода'
    );
  }

  openContextFolders(item: ProductAnalogueV2Item): void {
    void this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(FoldersComponent);
        dialogRef.componentInstance.product = product;
      },
      'Не удалось открыть папки товара'
    );
  }

  openContextProps(item: ProductAnalogueV2Item): void {
    void this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(PropertiesComponent);
        dialogRef.componentInstance.product = product;
      },
      'Не удалось открыть характеристики товара'
    );
  }

  async openContextQuickPrint(item: ProductAnalogueV2Item): Promise<void> {
    await this.withFullProduct(
      item,
      async (product) => {
        const dialogRef = this.dialog.open(LabelPrintComponent, { width: '480px', maxWidth: 'calc(100vw - 24px)' });
        dialogRef.componentInstance.productId = product.id;
        dialogRef.componentInstance.productTitle = `${product.brand?.description || ''} ${product.article || ''}`.trim();
        dialogRef.componentInstance.warehouseId = this.user?.defaultWarehouse?.id;
        dialogRef.componentInstance.barcode = product.barcodes?.find((row) => String(row?.barcode || '').trim())?.barcode || '';
      },
      'Не удалось открыть печать штрих-кода'
    );
  }

  async openContextImportFromExist(item: ProductAnalogueV2Item): Promise<void> {
    if (!item.product.article?.trim()) {
      this.notificationService.warning('У товара нет артикула для поиска на Exist');
      return;
    }

    if (this.importingFromExistIds.has(item.product.id)) {
      return;
    }

    this.importingFromExistIds.add(item.product.id);
    try {
      await this.runExistImport(item);
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось импортировать товар с Exist');
    } finally {
      this.importingFromExistIds.delete(item.product.id);
    }
  }

  async openContextTogglePublic(item: ProductAnalogueV2Item): Promise<void> {
    if (this.togglingPublicIds.has(item.product.id)) {
      return;
    }

    this.togglingPublicIds.add(item.product.id);
    try {
      await firstValueFrom(this.productService.togglePublic(item.product.id));
      this.productDetailsCache.delete(item.product.id);
      this.notificationService.notice('Статус продаж переключён');
      await this.reload();
    } catch (err) {
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось переключить статус продаж')
      );
    } finally {
      this.togglingPublicIds.delete(item.product.id);
    }
  }

  async toggleUpdateRetail(item: ProductAnalogueV2Item): Promise<void> {
    if (this.togglingUpdateRetailIds.has(item.product.id)) {
      return;
    }

    const nextValue = !item.product.updateRetail;
    this.togglingUpdateRetailIds.add(item.product.id);
    try {
      const updatedProduct = await firstValueFrom(
        this.productService.updateAutoRetail(item.product.id, nextValue)
      );
      item.product = {
        ...item.product,
        updateRetail: Boolean(updatedProduct.updateRetail)
      };
      this.productDetailsCache.delete(item.product.id);
      this.notificationService.notice(
        item.product.updateRetail ? 'Автообновление цены включено' : 'Автообновление цены выключено'
      );
    } catch (err) {
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось изменить автообновление цены')
      );
    } finally {
      this.togglingUpdateRetailIds.delete(item.product.id);
    }
  }

  openMove(item: ProductAnalogueV2Item): void {
    const dialogRef = this.dialog.open(MoveComponent, {
      width: '1500px',
      height: '800px'
    });
    dialogRef.componentInstance.product_id = item.product.id;
  }

  openStockDetails(item: ProductAnalogueV2Item): void {
    if (!this.user?.defaultWarehouse?.id) {
      this.notificationService.warning('У пользователя не выбран основной склад');
      return;
    }

    void this.withFullProduct(
      item,
      (product) => {
        const dialogRef = this.dialog.open(CatalogStockDialogComponent, {
          width: '720px',
          maxWidth: '95vw'
        });
        dialogRef.componentInstance.product = product;
        dialogRef.componentInstance.warehouse = this.user?.defaultWarehouse;
      },
      'Не удалось открыть адресное хранение'
    );
  }

  addToCart(item: ProductAnalogueV2Item): void {
    if (this.cartLoadingIds.has(item.product.id)) {
      return;
    }

    this.cartLoadingIds.add(item.product.id);
    void this.withFullProduct(
      item,
      (product) => {
        this.store.dispatch(addToCart({ payload: product }));
        this.notificationService.notice('Товар добавлен в корзину');
      },
      'Не удалось добавить товар в корзину'
    ).finally(() => {
      this.cartLoadingIds.delete(item.product.id);
    });
  }

  productInCart(productId: number): boolean {
    return this.cartProducts.some((row) => row.product.id === productId);
  }

  getProductCartQuantity(productId: number): number {
    return this.cartProducts.find((row) => row.product.id === productId)?.quan || 1;
  }

  changeCartQuan(item: ProductAnalogueV2Item, event: Event): void {
    const cartRow = this.cartProducts.find((row) => row.product.id === item.product.id);
    if (!cartRow) {
      return;
    }

    const nextValue = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(nextValue) || nextValue <= 0) {
      this.removeFromCart(item.product.id);
      return;
    }

    this.store.dispatch(
      updateProductInCart({
        payload: cartRow.product,
        quan: nextValue
      })
    );
  }

  removeFromCart(productId: number): void {
    const cartRow = this.cartProducts.find((row) => row.product.id === productId);
    if (!cartRow) {
      return;
    }

    this.store.dispatch(removeFromCart({ payload: cartRow.product }));
  }

  trackByProductId(_: number, item: ProductAnalogueV2Item) {
    return item.product.id;
  }

  private updateImagePreviewPosition(event: MouseEvent): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const previewSize = this.imagePreviewSizePx;
    const offset = this.imagePreviewOffsetPx;

    let nextLeft = event.clientX + offset;
    let nextTop = event.clientY + offset;

    if (nextLeft + previewSize > viewportWidth - 12) {
      nextLeft = Math.max(12, event.clientX - previewSize - offset);
    }

    if (nextTop + previewSize > viewportHeight - 12) {
      nextTop = Math.max(12, event.clientY - previewSize - offset);
    }

    this.hoveredImagePreviewLeft = nextLeft;
    this.hoveredImagePreviewTop = nextTop;
  }

  private buildProviderTile(
    key: 'in_city' | 'out_of_city',
    label: string,
    rows: ProductInProviderWarehouse[]
  ): AnalogueProviderTile {
    const quantity = rows.reduce((sum, row) => sum + Number(row.quan || 0), 0);
    const grouped = new Map<string, AnalogueProviderTooltipDetail>();

    rows.forEach((row) => {
      const providerName = row.providerWarehouse?.provider?.description || 'Поставщик';
      const price =
        row.price !== null &&
        row.price !== undefined &&
        Number.isFinite(Number(row.price)) &&
        Number(row.price) > 0
          ? Number(row.price)
          : null;
      const current = grouped.get(providerName) || {
        providerName,
        quantity: 0,
        price: null
      };
      current.quantity += Number(row.quan || 0);
      if (price !== null) {
        current.price = current.price === null ? price : Math.min(current.price, price);
      }
      grouped.set(providerName, current);
    });

    const details = this.getTopProviderTooltipDetails([...grouped.values()]).map(
      (detail) =>
        `${detail.providerName}: ${this.formatStockQuantity(
          detail.quantity
        )} • ${this.formatProviderPrice(detail.price)}`
    );

    return {
      key,
      label,
      quantity,
      quantityLabel: this.formatStockQuantity(quantity),
      tooltip: details.length
        ? [`${label}: ${this.formatStockQuantity(quantity)}`, ...details].join('\n')
        : `${label}: ${this.formatStockQuantity(quantity)}`
    };
  }

  private getTopProviderTooltipDetails(
    details: AnalogueProviderTooltipDetail[]
  ): AnalogueProviderTooltipDetail[] {
    return [...details]
      .sort((left, right) => {
        const priceDelta =
          (left.price ?? Number.MAX_SAFE_INTEGER) - (right.price ?? Number.MAX_SAFE_INTEGER);
        if (priceDelta !== 0) return priceDelta;

        return left.providerName.localeCompare(right.providerName, 'uk', {
          sensitivity: 'base',
          numeric: true
        });
      })
      .slice(0, 5);
  }

  private formatProviderPrice(price: number | null): string {
    return price === null ? 'цена не указана' : this.formatPriceHryvnia(price);
  }

  private getWarehouseLegend(): NewCatalogWarehouseLegend[] {
    return this.response?.warehouseLegend ?? [];
  }

  private getManagerCityId(): number | null {
    const primaryLegend = this.getWarehouseLegend().find(
      (warehouse) => warehouse.isPrimaryForManager
    );
    const legendCityId = Number(primaryLegend?.cityId || 0) || null;
    if (legendCityId !== null) {
      return legendCityId;
    }

    return Number(this.user?.defaultWarehouse?.city?.id || 0) || null;
  }

  private async getFullProduct(item: ProductAnalogueV2Item): Promise<Product> {
    const cached = this.productDetailsCache.get(item.product.id);
    if (cached) {
      return cached;
    }

    const product = await firstValueFrom(this.productService.getOne(item.product.id));
    this.productDetailsCache.set(item.product.id, product);
    return product;
  }

  private clearTileCaches(): void {
    this.stockTilesCache.clear();
    this.providerTilesCache.clear();
  }

  private async withFullProduct(
    item: ProductAnalogueV2Item,
    action: (product: Product) => Promise<void> | void,
    fallbackErrorMessage: string
  ): Promise<void> {
    try {
      const product = await this.getFullProduct(item);
      await action(product);
    } catch (err) {
      this.notificationService.warning(extractApiErrorMessage(err, fallbackErrorMessage));
    }
  }

  private async runExistImport(
    item: ProductAnalogueV2Item,
    selectedExistProductId?: number
  ): Promise<void> {
    const response = await firstValueFrom(
      this.productService.importFromExist(item.product.id, {
        selected_exist_product_id: selectedExistProductId
      })
    );

    if (response.status === 'not_found') {
      this.notificationService.warning('На Exist ничего не найдено по этому артикулу');
      return;
    }

    if (response.status === 'needs_selection') {
      const selected = await this.openExistSelectionDialog(item, response.candidates);
      if (!selected) return;
      return this.runExistImport(item, selected.exist_product_id);
    }

    this.productDetailsCache.delete(item.product.id);
    await this.reload();
    this.notificationService.notice(this.buildImportSummary(response));
  }

  private async openExistSelectionDialog(
    item: ProductAnalogueV2Item,
    candidates: ProductImportFromExistCandidate[]
  ) {
    const dialogRef = this.dialog.open(ExistImportSelectionDialogComponent, {
      width: '900px',
      maxWidth: '96vw',
      data: {
        article: item.product.article,
        candidates
      }
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  private buildImportSummary(result: ProductImportFromExistImportedResult) {
    const report = result.report;
    const parts: string[] = [];

    if (report.updated_ua_fields.length) {
      parts.push(`обновлены поля: ${report.updated_ua_fields.join(', ')}`);
    }
    if (report.added_images > 0) {
      parts.push(`добавлено картинок: ${report.added_images}`);
    }
    if (report.created_analog_products > 0 || report.linked_analog_products > 0) {
      parts.push(
        `аналоги: ${report.created_analog_products} созд., ${report.linked_analog_products} связ.`
      );
    }
    if (report.created_oe_products > 0 || report.linked_oe_products > 0) {
      parts.push(`OE: ${report.created_oe_products} созд., ${report.linked_oe_products} связ.`);
    }
    if (report.updated_exist_link) {
      parts.push('ссылка Exist сохранена');
    }
    if (report.skipped_duplicates > 0) {
      parts.push(`дубликаты пропущены: ${report.skipped_duplicates}`);
    }

    return parts.length
      ? `Импорт с Exist завершен: ${parts.join(' · ')}`
      : 'Импорт с Exist завершен без изменений';
  }

  private sortItems(left: ProductAnalogueV2Item, right: ProductAnalogueV2Item): number {
    const leftMetrics = this.getSortMetrics(left.product);
    const rightMetrics = this.getSortMetrics(right.product);

    const bucketDelta = this.compareAvailabilityBuckets(leftMetrics.bucket, rightMetrics.bucket);
    if (bucketDelta !== 0) {
      return bucketDelta;
    }

    const managerDelta = rightMetrics.managerStockTotal - leftMetrics.managerStockTotal;
    if (managerDelta !== 0) {
      return managerDelta;
    }

    const otherLocalDelta = rightMetrics.otherStockTotal - leftMetrics.otherStockTotal;
    if (otherLocalDelta !== 0) {
      return otherLocalDelta;
    }

    const providerInCityDelta = rightMetrics.providerInCityTotal - leftMetrics.providerInCityTotal;
    if (providerInCityDelta !== 0) {
      return providerInCityDelta;
    }

    const providerOutOfCityDelta =
      rightMetrics.providerOutOfCityTotal - leftMetrics.providerOutOfCityTotal;
    if (providerOutOfCityDelta !== 0) {
      return providerOutOfCityDelta;
    }

    const stockDelta = rightMetrics.stockTotal - leftMetrics.stockTotal;
    if (stockDelta !== 0) {
      return stockDelta;
    }

    if (leftMetrics.displayPrice !== rightMetrics.displayPrice) {
      return leftMetrics.displayPrice - rightMetrics.displayPrice;
    }

    return Number(right.product.id) - Number(left.product.id);
  }

  private getSortMetrics(product: Product): AnalogueSortMetrics {
    const defaultWarehouseId = Number(this.user?.defaultWarehouse?.id || 0);
    const managerCityId = Number(this.user?.defaultWarehouse?.city?.id || 0) || null;
    const stockRows = product.stock || [];
    const providerRows = product.providerStock || [];

    const managerStockTotal = stockRows.reduce((sum, row) => {
      if (Number(row.warehouse?.id) !== defaultWarehouseId) {
        return sum;
      }
      return sum + Number(row.quan || 0);
    }, 0);

    const otherStockTotal = stockRows.reduce((sum, row) => {
      if (Number(row.warehouse?.id) === defaultWarehouseId) {
        return sum;
      }
      return sum + Number(row.quan || 0);
    }, 0);

    const stockTotal = stockRows.reduce((sum, row) => sum + Number(row.quan || 0), 0);

    const providerInCityTotal = providerRows.reduce((sum, row) => {
      if (
        managerCityId !== null &&
        Number(row.providerWarehouse?.city?.id || 0) === managerCityId
      ) {
        return sum + Number(row.quan || 0);
      }
      return sum;
    }, 0);

    const providerOutOfCityTotal = providerRows.reduce((sum, row) => {
      if (managerCityId === null) {
        return sum + Number(row.quan || 0);
      }
      if (Number(row.providerWarehouse?.city?.id || 0) !== managerCityId) {
        return sum + Number(row.quan || 0);
      }
      return sum;
    }, 0);

    return {
      bucket: this.resolveAvailabilityBucket(
        managerStockTotal,
        otherStockTotal,
        providerInCityTotal,
        providerOutOfCityTotal
      ),
      managerStockTotal,
      otherStockTotal,
      providerInCityTotal,
      providerOutOfCityTotal,
      stockTotal,
      displayPrice: Number(product.retail ?? product.price ?? Number.MAX_SAFE_INTEGER)
    };
  }

  private resolveAvailabilityBucket(
    managerStockTotal: number,
    otherStockTotal: number,
    providerInCityTotal: number,
    providerOutOfCityTotal: number
  ): AnalogueAvailabilityBucket {
    if (managerStockTotal > 0) {
      return 'MANAGER_STOCK';
    }

    if (otherStockTotal > 0) {
      return 'OTHER_STOCK';
    }

    if (providerInCityTotal > 0) {
      return 'PROVIDER_IN_CITY';
    }

    if (providerOutOfCityTotal > 0) {
      return 'PROVIDER_OUT_OF_CITY';
    }

    return 'NO_STOCK';
  }

  private compareAvailabilityBuckets(
    left: AnalogueAvailabilityBucket,
    right: AnalogueAvailabilityBucket
  ): number {
    const bucketOrder: Record<AnalogueAvailabilityBucket, number> = {
      MANAGER_STOCK: 0,
      OTHER_STOCK: 1,
      PROVIDER_IN_CITY: 2,
      PROVIDER_OUT_OF_CITY: 3,
      NO_STOCK: 4
    };

    return bucketOrder[left] - bucketOrder[right];
  }

  private formatStockQuantity(quantity: number) {
    return Number(quantity || 0).toLocaleString('ru-RU', {
      maximumFractionDigits: 2
    });
  }

  private buildProductSummaryClipboardText(item: ProductAnalogueV2Item): string {
    const name = String(item.product.description || '').trim();
    const brandName = String(item.product.brand?.description || '').trim();
    const article = String(item.product.article || '').trim();
    const formattedArticle = article ? `*${article}*` : '';
    const price = this.formatPriceHryvnia(this.getDisplayPrice(item.product));
    const secondLineMain = [brandName, formattedArticle].filter((part) => Boolean(part)).join(' ');
    const priceLine = `\`\`\`${price}\`\`\``;

    return [name, secondLineMain, priceLine].filter((part) => Boolean(part)).join('\n');
  }

  private async writeToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {}

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } catch (error) {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
